import { useRef, useState } from "react";
import { api, formatApiError, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

/**
 * Image upload + preview field.
 * - Pasting a URL still works (manual input)
 * - "Upload" button uploads a file to /api/admin/upload and fills the URL
 */
export default function ImageUpload({ value, onChange, label = "Image URL", testId = "image-upload", required }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  // Resolve relative /api/uploads URLs to full URL for preview
  const previewSrc = resolveImage(value);

  return (
    <div>
      <label className="text-eyebrow text-ink-muted block mb-2">{label}</label>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          data-testid={`${testId}-url`}
          type="text"
          value={value ?? ""}
          required={required}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste URL or upload a file"
          className="flex-1 bg-bg border border-edge px-4 py-3 text-ink focus:outline-none focus:border-brand"
        />
        <label
          data-testid={`${testId}-button`}
          className={`inline-flex items-center justify-center gap-2 px-5 py-3 border border-edge text-sm cursor-pointer transition-colors ${
            uploading ? "opacity-60 cursor-wait" : "hover:border-brand hover:text-brand"
          }`}
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {uploading ? "Uploading…" : "Upload"}
          <input
            ref={inputRef}
            data-testid={`${testId}-file`}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/heic"
            className="hidden"
            onChange={onFile}
            disabled={uploading}
          />
        </label>
      </div>
      {previewSrc && (
        <div className="mt-3 w-32 h-32 border border-edge overflow-hidden bg-bg-surface">
          <img src={previewSrc} alt="" className="w-full h-full object-cover" />
        </div>
      )}
    </div>
  );
}
